import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { DashboardLayout } from '@/components/DashboardLayout';
import { useAuth } from '@/context/AuthContext';
import { FileText, Upload, Trash2, CheckCircle, Clock, RefreshCw, AlertCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

function fmtDate(ts) {
  if (!ts) return '—';
  return new Date(ts).toLocaleString('en-IN', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });
}

function fmtSize(bytes) {
  if (!bytes) return '—';
  if (bytes < 1024) return bytes + ' B';
  if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
  return (bytes / (1024 * 1024)).toFixed(2) + ' MB';
}

export default function AdminDocuments() {
  const { user } = useAuth();
  const [msaDocs, setMsaDocs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [deleting, setDeleting] = useState(null);
  const fileInputRef = useRef(null);

  const fetchMsaDocs = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/admin/msa`);
      setMsaDocs(res.data || []);
    } catch {
      setMsaDocs([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchMsaDocs(); }, []);

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.type !== 'application/pdf') {
      toast.error('Only PDF files are allowed for MSA');
      return;
    }
    if (file.size > 25 * 1024 * 1024) {
      toast.error('File too large. Max 25 MB allowed.');
      return;
    }
    setUploading(true);
    const formData = new FormData();
    formData.append('file', file);
    try {
      await axios.post(`${API}/admin/msa`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      toast.success('MSA uploaded and set as active!');
      fetchMsaDocs();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Upload failed');
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const handleDelete = async (docId) => {
    if (!window.confirm('Delete this MSA document? This cannot be undone.')) return;
    setDeleting(docId);
    try {
      await axios.delete(`${API}/admin/msa/${docId}`);
      toast.success('MSA document deleted');
      fetchMsaDocs();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Delete failed');
    } finally {
      setDeleting(null);
    }
  };

  const activeDoc = msaDocs.find(d => d.is_active);

  return (
    <DashboardLayout role={user?.role}>
      <div className="space-y-6">
        {/* Page Header */}
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div>
            <h1 className="text-2xl font-bold text-slate-800">Documents</h1>
            <p className="text-sm text-slate-500 mt-0.5">Manage MSA agreements and compliance documents</p>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" onClick={fetchMsaDocs} disabled={loading} data-testid="refresh-msa-btn">
              <RefreshCw className={`h-4 w-4 mr-1.5 ${loading ? 'animate-spin' : ''}`} />
              Refresh
            </Button>
            <input
              ref={fileInputRef}
              type="file"
              accept="application/pdf,.pdf"
              className="hidden"
              onChange={handleUpload}
              data-testid="msa-file-input"
            />
            <Button
              onClick={() => fileInputRef.current?.click()}
              disabled={uploading}
              className="bg-blue-600 hover:bg-blue-700 text-white"
              data-testid="upload-msa-btn"
            >
              {uploading ? (
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2" />
              ) : (
                <Upload className="h-4 w-4 mr-2" />
              )}
              {uploading ? 'Uploading…' : 'Upload MSA PDF'}
            </Button>
          </div>
        </div>

        {/* Currently Active MSA banner */}
        {activeDoc && (
          <Card className="border-green-200 bg-green-50/30" data-testid="active-msa-banner">
            <CardContent className="p-4 flex items-center gap-4">
              <CheckCircle className="h-8 w-8 text-green-500 flex-shrink-0" />
              <div className="flex-1 min-w-0">
                <p className="text-sm font-semibold text-green-800">Currently Active MSA</p>
                <p className="text-sm text-green-700 truncate">{activeDoc.file_name}</p>
                <p className="text-xs text-slate-500 mt-0.5">Uploaded {fmtDate(activeDoc.uploaded_at)} · {fmtSize(activeDoc.file_size)}</p>
              </div>
              <div className="flex items-center gap-2 flex-shrink-0">
                <a
                  href={`${API}/msa/active/download`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1.5 text-xs text-blue-600 border border-blue-200 rounded-md px-2.5 py-1 hover:bg-blue-50 transition-colors"
                  data-testid="preview-active-msa"
                >
                  <FileText className="h-3.5 w-3.5" /> Preview / Download
                </a>
              </div>
            </CardContent>
          </Card>
        )}

        {/* MSA Documents Table */}
        <div>
          <h2 className="text-base font-semibold text-slate-700 mb-3">All MSA Documents</h2>
          <Card>
            <CardContent className="p-0">
              {loading ? (
                <div className="flex justify-center py-20">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" />
                </div>
              ) : msaDocs.length === 0 ? (
                <div className="flex flex-col items-center justify-center py-20 text-center">
                  <div className="h-14 w-14 bg-slate-100 rounded-full flex items-center justify-center mb-4">
                    <FileText className="h-7 w-7 text-slate-300" />
                  </div>
                  <p className="text-sm font-medium text-slate-500">No MSA documents uploaded yet</p>
                  <p className="text-xs text-slate-400 mt-1">Click "Upload MSA PDF" to add the first document.</p>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm" data-testid="msa-docs-table">
                    <thead className="bg-slate-50 border-b border-slate-200">
                      <tr>
                        <th className="px-5 py-3.5 text-left text-xs font-semibold text-slate-500 uppercase tracking-wide">File Name</th>
                        <th className="px-5 py-3.5 text-left text-xs font-semibold text-slate-500 uppercase tracking-wide">Size</th>
                        <th className="px-5 py-3.5 text-left text-xs font-semibold text-slate-500 uppercase tracking-wide">Uploaded</th>
                        <th className="px-5 py-3.5 text-left text-xs font-semibold text-slate-500 uppercase tracking-wide">Status</th>
                        <th className="px-5 py-3.5 text-right text-xs font-semibold text-slate-500 uppercase tracking-wide">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100">
                      {msaDocs.map((doc) => (
                        <tr key={doc.id} className="hover:bg-slate-50 transition-colors" data-testid={`msa-doc-row-${doc.id}`}>
                          <td className="px-5 py-4">
                            <div className="flex items-center gap-3">
                              <div className="h-9 w-9 bg-red-50 rounded-lg flex items-center justify-center flex-shrink-0">
                                <FileText className="h-4 w-4 text-red-500" />
                              </div>
                              <div className="min-w-0">
                                <p className="text-slate-800 font-medium truncate max-w-[260px]">{doc.file_name}</p>
                                {doc.uploaded_by_email && (
                                  <p className="text-xs text-slate-400 mt-0.5">{doc.uploaded_by_email}</p>
                                )}
                              </div>
                            </div>
                          </td>
                          <td className="px-5 py-4 text-slate-500 text-xs">{fmtSize(doc.file_size)}</td>
                          <td className="px-5 py-4 text-slate-500 text-xs whitespace-nowrap">{fmtDate(doc.uploaded_at)}</td>
                          <td className="px-5 py-4">
                            {doc.is_active ? (
                              <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-semibold bg-green-100 text-green-700 border border-green-200">
                                <CheckCircle className="h-3 w-3" /> Active
                              </span>
                            ) : (
                              <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-semibold bg-slate-100 text-slate-500 border border-slate-200">
                                <Clock className="h-3 w-3" /> Inactive
                              </span>
                            )}
                          </td>
                          <td className="px-5 py-4 text-right">
                            <div className="flex items-center justify-end gap-2">
                              <Button
                                size="sm"
                                variant="ghost"
                                className="text-red-600 hover:bg-red-50 h-8 px-2"
                                onClick={() => handleDelete(doc.id)}
                                disabled={deleting === doc.id}
                                data-testid={`delete-msa-${doc.id}`}
                              >
                                {deleting === doc.id ? (
                                  <div className="animate-spin rounded-full h-3.5 w-3.5 border-b-2 border-red-500" />
                                ) : (
                                  <Trash2 className="h-3.5 w-3.5" />
                                )}
                              </Button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Info banner */}
        <Card className="border-blue-100 bg-blue-50/40">
          <CardContent className="p-4 flex items-start gap-3">
            <AlertCircle className="h-5 w-5 text-blue-500 flex-shrink-0 mt-0.5" />
            <div className="text-sm text-blue-700">
              <p className="font-medium mb-1">About MSA Documents</p>
              <ul className="list-disc list-inside text-xs space-y-0.5 text-blue-600">
                <li>Only <strong>one</strong> MSA can be active at a time. Uploading a new MSA automatically deactivates the previous one.</li>
                <li>Partners can view and download the active MSA from their Documents tab in the KYC Portal.</li>
                <li>Deleting the active MSA will make the MSA unavailable to all partners until a new one is uploaded.</li>
              </ul>
            </div>
          </CardContent>
        </Card>
      </div>
    </DashboardLayout>
  );
}
